// =============================================================================
// Backend URL Resolver — Runtime, Portable, Platform-Agnostic
// -----------------------------------------------------------------------------
// This function decides which backend API to call based on WHERE the frontend
// is currently running — NOT based on any build-time environment variable.
//
// Why runtime, not build-time?
//   Build-time env vars (REACT_APP_BACKEND_URL) get baked into the compiled
//   JS bundle when a platform runs `yarn build`. Every deploy target
//   (Cloudflare, Vercel, Netlify, self-host) handles env vars differently:
//   dashboard variables, .env files, .env.production files, CI secrets —
//   and they take precedence over each other in confusing ways. When any
//   platform's config drifts stale, the bundle silently bakes in a dead URL
//   and every API call 404s.
//
//   By resolving at RUNTIME based on window.location.hostname, we sidestep
//   every platform's env-var config entirely. The same compiled bundle
//   works everywhere with zero per-platform setup.
//
// Rules:
//   localhost / 127.0.0.1  ->  build-time env (dev) OR http://localhost:8001
//   *.preview.emergentagent.com  ->  build-time env (Emergent preview backend)
//   callsheet.mov / www.  ->  same-origin (production stack)
//   anything else (Cloudflare, Vercel, custom domain)  ->  callsheet.mov
// =============================================================================

const PRODUCTION_BACKEND = "https://callsheet.mov";

export function getBackendUrl() {
  // SSR / non-browser context (should not happen in CRA but defensive)
  if (typeof window === "undefined") {
    return process.env.REACT_APP_BACKEND_URL || PRODUCTION_BACKEND;
  }

  const host = window.location.hostname;

  // Local development
  if (host === "localhost" || host === "127.0.0.1") {
    return process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";
  }

  // Emergent preview environments — trust the build-time env value which is
  // auto-set by Emergent to the paired preview backend URL
  if (host.endsWith(".preview.emergentagent.com") || host.endsWith(".emergentagent.com")) {
    return process.env.REACT_APP_BACKEND_URL || `${window.location.protocol}//${host}`;
  }

  // Emergent production (canonical + www)
  if (host === "callsheet.mov" || host === "www.callsheet.mov") {
    return PRODUCTION_BACKEND;
  }

  // Any other host (Cloudflare Workers, Vercel, Netlify, custom domains, mirrors)
  // → always route to the stable production backend
  return PRODUCTION_BACKEND;
}
